import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIIconButton from 'UIComponents/button/UIIconButton';
import H2 from 'UIComponents/elements/headings/H2';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITextInput from 'UIComponents/input/UITextInput';
import UIBox from 'UIComponents/layout/UIBox';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import { handleError } from 'commerce-subscription-lib/public/utilities';
import { useCallback, useState } from 'react';
import { useUpdateSubscriptionName } from '../hooks/useUpdateSubscriptionName';
import { useTracker } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function EditableTitle({
  title,
  objectId,
  isEditable
}) {
  const [name, setName] = useState(title);
  const [isEditing, setIsEditing] = useState(false);
  const {
    trackUsage,
    trackInteraction
  } = useTracker(parseInt(objectId, 10));
  const handleOpenChangeEditForm = useCallback(({
    target: {
      value
    }
  }) => {
    setIsEditing(!!value);
  }, [setIsEditing]);
  const [onSubmit, {
    loading
  }] = useUpdateSubscriptionName(objectId, name.trim());
  const saveChanges = useCallback(() => {
    setIsEditing(false);
    onSubmit().catch(handleError);
    trackUsage('renameSubscriptionSubmitted');
  }, [onSubmit, trackUsage]);
  const cancelChanges = useCallback(() => {
    setName(title);
    setIsEditing(false);
  }, [title, setName, setIsEditing]);
  const startEditing = useCallback(() => {
    setIsEditing(e => !e);
    trackInteraction('renameSubscriptionClicked');
  }, [setIsEditing, trackInteraction]);
  const handleKeyPress = useCallback(({
    key
  }) => {
    if (key === 'Enter') {
      saveChanges();
    }
  }, [saveChanges]);
  const isNameInvalid = name === '' || name.trim() === '' || title === name;
  return /*#__PURE__*/_jsxs(UIFlex, {
    align: "center",
    className: "p-right-3",
    children: [/*#__PURE__*/_jsx(UIBox, {
      grow: 1,
      children: /*#__PURE__*/_jsx(H2, {
        className: "p-y-1 p-4-x m-bottom-1 m-left-4",
        children: name
      })
    }), isEditable && /*#__PURE__*/_jsx(UIBox, {
      children: /*#__PURE__*/_jsx(UIPopover, {
        open: isEditing,
        onOpenChange: handleOpenChangeEditForm,
        placement: "bottom left",
        content: {
          body: /*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.namePlaceholder"
            }),
            children: /*#__PURE__*/_jsx(UITextInput, {
              value: name,
              onKeyPress: handleKeyPress,
              onChange: ({
                target: {
                  value
                }
              }) => {
                if (value !== '' && value.trim() === '') {
                  return;
                }
                setName(value);
              }
            })
          }),
          footer: /*#__PURE__*/_jsxs(UIButtonWrapper, {
            children: [/*#__PURE__*/_jsx(UIButton, {
              disabled: loading || isNameInvalid,
              onClick: saveChanges,
              size: "small",
              use: "tertiary",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.save"
              })
            }), /*#__PURE__*/_jsx(UIButton, {
              onClick: cancelChanges,
              size: "small",
              use: "tertiary-light",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.cancel"
              })
            })]
          })
        },
        children: /*#__PURE__*/_jsx(UIIconButton, {
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.edit"
          }),
          placement: "right",
          disabled: loading || isEditing,
          size: "small",
          use: "transparent",
          onClick: startEditing,
          "data-selenium-test": "highlight-editor-icon",
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "edit",
            screenReaderText: I18n.text('subscriptions.edit')
          })
        })
      })
    })]
  });
}
export default EditableTitle;